import { Box, Button, Flex, Grid, Input, Skeleton, Spinner, Stack, Text } from "@chakra-ui/react";
import { useList } from "@refinedev/core";
import { Activity, Bot, CalendarClock, CheckCircle2, CirclePause, Clock3, DatabaseZap, KeyRound, Landmark, LockKeyhole, Plus, Radio, RefreshCw, ServerCog, ShieldCheck, TimerReset, TriangleAlert, Wifi } from "lucide-react";
import { useState } from "react";
import { DataTable, type Column } from "../components/tables";
import { DataBar, EmptyState, MetricCard, PageHeader, Panel, StatusPill } from "../components/ui";
import { apiRequest } from "../providers/api-client";
import { CapitalAdvisoryPanel } from "../components/capital-advisory";
import type { BrokerConnectionRecord, BrokerRecord, DataQualityRecord } from "../types/domain";

type BrokerAdapterView = { botId: string; running: boolean; status: string };
type BrokerView = BrokerRecord & {
  probeBotId?: string;
  botIds?: string[];
  botCount?: number;
  runningBots?: number;
  running?: boolean;
  accessRights?: string;
  isLive?: boolean;
  authenticated?: boolean;
  transport?: string;
  subscriptionActive?: boolean;
  adapters?: BrokerAdapterView[];
  error?: string;
};

export function BrokersPage() {
  const { result, query } = useList<BrokerView>({ resource: "brokers", pagination: { currentPage: 1, pageSize: 50 }, liveMode: "off" });
  const connectionsQuery = useList<BrokerConnectionRecord>({
    resource: "brokerConnections",
    pagination: { currentPage: 1, pageSize: 100 },
    liveMode: "off",
    queryOptions: { refetchInterval: 5_000 },
  });
  const [connectingBotId, setConnectingBotId] = useState<string>();
  const [connectingConnectionId, setConnectingConnectionId] = useState<string>();
  const [connectionName, setConnectionName] = useState("");
  const [connectionEnvironment, setConnectionEnvironment] = useState<"DEMO" | "LIVE">("DEMO");
  const [creatingConnection, setCreatingConnection] = useState(false);
  const [connectError, setConnectError] = useState<string>();
  const rows = result.data ?? [];
  const connectionCount = rows.length;
  const connectedCount = rows.filter((broker) => broker.status === "CONNECTED").length;
  const botCount = rows.reduce((total, broker) => total + (broker.botCount ?? broker.botIds?.length ?? 0), 0);
  const runningBots = rows.reduce((total, broker) => total + (broker.runningBots ?? (broker.running ? 1 : 0)), 0);
  const lastProbe = rows.map((broker) => Date.parse(broker.heartbeat)).filter(Number.isFinite).sort((a, b) => b - a)[0];
  const oauthStatus = typeof window === "undefined" ? null : new URLSearchParams(window.location.search).get("ctraderOAuth");
  const connections = connectionsQuery.result.data ?? [];

  const openConnectionOAuth = async (connectionId: string) => {
    setConnectingConnectionId(connectionId);
    setConnectError(undefined);
    try {
      const result = await apiRequest<{ authorizationUrl: string }>("/trading/ctrader/oauth/start", {
        method: "POST",
        body: JSON.stringify({ connectionId }),
      });
      window.location.assign(result.authorizationUrl);
    } catch (error) {
      setConnectError(error instanceof Error ? error.message : String(error));
      setConnectingConnectionId(undefined);
    }
  };

  const addConnection = async () => {
    const name = connectionName.trim();
    if (!name) {
      setConnectError("Enter a connection label, such as your name or the account owner's name.");
      return;
    }
    setCreatingConnection(true);
    setConnectError(undefined);
    try {
      const connection = await apiRequest<BrokerConnectionRecord>("/trading/broker-connections", {
        method: "POST",
        body: JSON.stringify({ name, provider: "ctrader", environment: connectionEnvironment.toLowerCase() }),
      });
      await openConnectionOAuth(connection.id);
      setCreatingConnection(false);
    } catch (error) {
      setConnectError(error instanceof Error ? error.message : String(error));
      setCreatingConnection(false);
    }
  };

  const connectLive = async (botId: string) => {
    setConnectingBotId(botId);
    setConnectError(undefined);
    try {
      const result = await apiRequest<{ authorizationUrl: string }>("/trading/ctrader/oauth/start", {
        method: "POST",
        body: JSON.stringify({ botId }),
      });
      window.location.assign(result.authorizationUrl);
    } catch (error) {
      setConnectError(error instanceof Error ? error.message : String(error));
      setConnectingBotId(undefined);
    }
  };

  return (
    <Stack gap="5">
      <PageHeader
        title="cTrader Broker"
        description="One connection per broker account, with health and bot consumers verified by the server."
        actions={
          <>
            <StatusPill tone={query.isLoading ? "neutral" : connectedCount === connectionCount && connectionCount > 0 ? "positive" : connectedCount > 0 ? "warning" : "negative"}>
              {query.isLoading ? "CONNECTING" : connectionCount === 0 ? "NO CONNECTION" : `${connectedCount}/${connectionCount} CONNECTED`}
            </StatusPill>
            <Button variant="outline" size="sm" onClick={() => query.refetch()} disabled={query.isFetching} minW="118px">
              {query.isFetching ? <><Spinner size="xs" />Probing...</> : <><Box as={RefreshCw} boxSize="4" />Probe now</>}
            </Button>
          </>
        }
      />

      {query.isFetching && !query.isLoading && (
        <Flex px="3.5" py="2.5" borderWidth="1px" borderColor="rgba(59,130,246,.24)" bg="rgba(59,130,246,.07)" borderRadius="4px" align="center" gap="2.5">
          <Spinner size="xs" color="status.info" />
          <Text fontSize="xs" color="fg.muted">Refreshing cTrader authentication and account health...</Text>
        </Flex>
      )}

      {oauthStatus && (
        <Flex px="3.5" py="2.5" borderWidth="1px" borderColor={oauthStatus === "success" ? "rgba(34,197,94,.28)" : "rgba(244,63,94,.28)"} bg={oauthStatus === "success" ? "rgba(34,197,94,.07)" : "rgba(244,63,94,.07)"} borderRadius="4px" align="center" gap="2.5">
          <Box as={oauthStatus === "success" ? CheckCircle2 : TriangleAlert} boxSize="4" color={oauthStatus === "success" ? "status.positive" : "status.negative"} />
          <Text fontSize="xs" color="fg.muted">
            {oauthStatus === "success"
              ? "cTrader OAuth completed. The server is probing accounts in the selected environment now."
              : oauthStatus === "invalid"
                ? "The cTrader OAuth callback was missing or expired. Start the connection flow again."
                : "cTrader OAuth could not be completed. The broker connection remains disconnected."}
          </Text>
        </Flex>
      )}

      {connectError && (
        <Flex px="3.5" py="2.5" borderWidth="1px" borderColor="rgba(244,63,94,.28)" bg="rgba(244,63,94,.07)" borderRadius="4px" align="center" gap="2.5">
          <Box as={TriangleAlert} boxSize="4" color="status.negative" />
          <Text fontSize="xs" color="status.negative">{connectError}</Text>
        </Flex>
      )}

      <Panel icon={KeyRound} title="OAuth connections" action={<StatusPill>{connections.length} CONNECTIONS</StatusPill>}>
        <Text fontSize="xs" color="fg.muted" lineHeight="1.7">
          Create one connection for each cTrader owner/login. OAuth tokens stay encrypted on the server; bots select an authorized account, never a token.
        </Text>
        <Flex mt="4" gap="2.5" direction={{ base: "column", md: "row" }} align={{ md: "center" }}>
          <Input
            value={connectionName}
            onChange={(event) => setConnectionName(event.target.value)}
            placeholder="Connection label, e.g. Chuong LIVE or Client A"
            bg="bg.subtle"
            borderColor="border.default"
            maxW={{ md: "420px" }}
          />
          <Flex gap="1.5" p="1" bg="bg.subtle" borderWidth="1px" borderColor="border.default" borderRadius="4px">
            {(["DEMO", "LIVE"] as const).map((environment) => (
              <Button
                key={environment}
                size="sm"
                variant={connectionEnvironment === environment ? "solid" : "ghost"}
                bg={connectionEnvironment === environment ? (environment === "LIVE" ? "status.negative" : "status.warning") : undefined}
                color={connectionEnvironment === environment ? "white" : "fg.muted"}
                onClick={() => setConnectionEnvironment(environment)}
              >
                {environment}
              </Button>
            ))}
          </Flex>
          <Button onClick={() => void addConnection()} disabled={creatingConnection || Boolean(connectingConnectionId)} bg="brand.500" color="white">
            {creatingConnection ? <><Spinner size="xs" />Creating...</> : <><Box as={Plus} boxSize="4" />Add {connectionEnvironment} connection</>}
          </Button>
        </Flex>
        <Grid mt="4" templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap="2.5">
          {connections.map((connection) => (
            <Flex key={connection.id} p="3.5" bg="bg.subtle" borderRadius="4px" align={{ base: "start", md: "center" }} gap="3" direction={{ base: "column", md: "row" }}>
              <Box flex="1" minW="0">
                <Flex align="center" gap="2" flexWrap="wrap">
                  <Text fontSize="sm" fontWeight="800">{connection.name}</Text>
                  <StatusPill tone={connection.status === "AUTHORIZED" ? "positive" : connection.status === "ERROR" ? "negative" : "neutral"}>{connection.status}</StatusPill>
                  <StatusPill tone="negative">{connection.environment}</StatusPill>
                </Flex>
                <Text mt="1" fontSize="10px" color="fg.muted">{connection.accountCount} authorized account(s) · {connection.botCount} bot consumer(s)</Text>
                {connection.accounts.map((account) => (
                  <Text key={account.id} mt="1.5" fontSize="10px" color="fg.muted" fontFamily="mono">
                    {account.account} · {account.brokerName ?? "cTrader"} · {account.environment}
                  </Text>
                ))}
                {connection.lastError && <Text mt="1.5" fontSize="10px" color="status.negative">{connection.lastError}</Text>}
              </Box>
              <Button size="sm" variant="outline" onClick={() => void openConnectionOAuth(connection.id)} disabled={Boolean(connectingConnectionId)}>
                {connectingConnectionId === connection.id ? <><Spinner size="xs" />Opening...</> : <><Box as={KeyRound} boxSize="3.5" />{connection.status === "AUTHORIZED" ? "Reconnect" : "Authorize"}</>}
              </Button>
            </Flex>
          ))}
        </Grid>
      </Panel>

      {query.error && (
        <Panel icon={Activity} title="Connection probe failed" action={<StatusPill tone="negative">OFFLINE</StatusPill>}>
          <Text color="status.negative" fontSize="xs">{query.error.message}</Text>
        </Panel>
      )}

      {connections.flatMap(c => c.accounts).filter(a => a.isLive).map(a => (
        <CapitalAdvisoryPanel key={a.id} accountId={a.id} label={a.account} />
      ))}

      {query.isLoading ? (
        <BrokerLoadingState />
      ) : rows.length === 0 ? (
        <Panel>
          <EmptyState
            icon={Landmark}
            title={connections.some((connection) => connection.accountCount > 0) ? "Broker account ready" : "No cTrader account configured"}
            description={connections.some((connection) => connection.accountCount > 0)
              ? "No bot uses this account yet. Create a LIVE bot and select the authorized account in Trading Mode."
              : "Add a cTrader OAuth connection above before creating a LIVE bot."}
          />
        </Panel>
      ) : (
        <>
          <Grid templateColumns={{ base: "1fr 1fr", lg: "repeat(4,1fr)" }} gap="3">
            <MetricCard icon={Landmark} label="Broker accounts" value={String(connectionCount)} detail="Unique cTrader account connections" />
            <MetricCard icon={CheckCircle2} label="Connected" value={`${connectedCount} / ${connectionCount}`} detail={connectedCount === connectionCount ? "All account probes healthy" : "Connection attention required"} />
            <MetricCard icon={Bot} label="Bot consumers" value={`${runningBots} / ${botCount}`} detail="Running / registered on cTrader" />
            <MetricCard icon={Clock3} label="Last probe" value={lastProbe ? formatProbeTime(lastProbe) : "—"} detail="Server-confirmed health check" />
          </Grid>

          <Flex bg="rgba(59,130,246,.06)" borderWidth="1px" borderColor="rgba(59,130,246,.2)" borderRadius="4px" p="4" gap="3" align={{ base: "start", md: "center" }} direction={{ base: "column", md: "row" }}>
            <Flex boxSize="34px" flexShrink="0" align="center" justify="center" borderRadius="4px" bg="rgba(59,130,246,.12)" color="status.info"><Box as={ShieldCheck} boxSize="4" /></Flex>
            <Box flex="1"><Text fontSize="sm" fontWeight="750">Broker accounts are not duplicated per bot</Text><Text mt="1" fontSize="xs" color="fg.muted">Bots that share the same cTrader environment and account are grouped into one connection. Their individual runtime states remain visible as consumers below.</Text></Box>
            <StatusPill tone="info">{botCount} BOT CONSUMERS</StatusPill>
          </Flex>

          <Stack gap="4" opacity={query.isFetching ? 0.72 : 1} transition="opacity .18s ease">
            {rows.map((broker) => (
              <BrokerConnectionCard
                key={broker.id}
                broker={broker}
                connecting={connectingBotId === broker.probeBotId}
                onConnect={broker.probeBotId ? () => void connectLive(broker.probeBotId!) : undefined}
              />
            ))}
          </Stack>
        </>
      )}

      <Panel icon={LockKeyhole} title="Credential boundary" action={<StatusPill>SERVER ONLY</StatusPill>}>
        <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap="3">
          <CredentialFact icon={KeyRound} title="OAuth credentials" detail="Access and refresh tokens are encrypted per broker connection in the database and are never returned to this UI." />
          <CredentialFact icon={ServerCog} title="Account context" detail="The UI receives only a masked account ID and sanitized health metadata after the server probe succeeds." />
        </Grid>
        <Text mt="3" fontSize="10px" color="fg.subtle" fontFamily="mono">ENV: CTRADER_CLIENT_ID · CTRADER_CLIENT_SECRET · TRADING_SECRET_ENCRYPTION_KEY</Text>
      </Panel>
    </Stack>
  );
}

function BrokerConnectionCard({ broker, connecting, onConnect }: { broker: BrokerView; connecting?: boolean; onConnect?: () => void }) {
  const adapters = broker.adapters ?? (broker.botIds ?? []).map((botId) => ({ botId, running: botId === broker.probeBotId && Boolean(broker.running), status: botId === broker.probeBotId && broker.running ? "running" : "paused" }));
  const tone = broker.status === "CONNECTED" ? "positive" : broker.status === "DEGRADED" ? "warning" : "negative";
  return (
    <Panel icon={Landmark} title="cTrader Open API" action={<StatusPill tone={tone}>{broker.status}</StatusPill>}>
      <Grid templateColumns={{ base: "1fr", lg: "minmax(0,.85fr) minmax(420px,1.15fr)" }} gap="5" alignItems="start">
        <Stack gap="3">
          <Box><Text fontSize="10px" color="fg.subtle" textTransform="uppercase" letterSpacing=".08em">Execution account</Text><Text mt="1" fontFamily="mono" fontSize="2xl" fontWeight="800">{broker.account}</Text></Box>
          <Flex gap="2" flexWrap="wrap">
            <StatusPill tone={broker.isLive ? "negative" : "warning"}>{String(broker.environment).toUpperCase()} ACCOUNT</StatusPill>
            <StatusPill tone={broker.authenticated === true ? "positive" : broker.authenticated === false ? "negative" : "neutral"}>
              {broker.authenticated === true ? "AUTHENTICATED" : broker.authenticated === false ? "AUTH FAILED" : "AUTH UNAVAILABLE"}
            </StatusPill>
            <StatusPill tone={broker.subscriptionActive ? "positive" : "neutral"}>{broker.subscriptionActive ? "MARKET STREAM ACTIVE" : "STREAM IDLE"}</StatusPill>
          </Flex>
          <Text fontSize="xs" color="fg.muted" lineHeight="1.7">This masked account is shared by {broker.botCount ?? adapters.length} registered bot adapters. The server probes the active adapter once and reports the connection as a single broker account.</Text>
          {broker.error && <Text p="3" borderRadius="4px" bg="rgba(244,63,94,.08)" color="status.negative" fontSize="xs" overflowWrap="anywhere">{broker.error}</Text>}
          {String(broker.environment).toLowerCase() === "live" && broker.status !== "CONNECTED" && onConnect && (
            <Button onClick={onConnect} disabled={connecting} bg="status.negative" color="white" _hover={{ opacity: 0.9 }}>
              {connecting ? <><Spinner size="xs" />Opening cTrader...</> : <><Box as={KeyRound} boxSize="4" />Connect cTrader LIVE</>}
            </Button>
          )}
        </Stack>
        <Grid templateColumns={{ base: "1fr 1fr", md: "repeat(3,1fr)" }} gap="2">
          <ConnectionFact label="Environment" value={String(broker.environment).toUpperCase()} />
          <ConnectionFact label="Access rights" value={(broker.accessRights ?? "Unavailable").toUpperCase()} />
          <ConnectionFact label="Transport" value={(broker.transport ?? (broker.status === "CONNECTED" ? "connected" : "unavailable")).toUpperCase()} />
          <ConnectionFact label="Probe duration" value={broker.latency > 0 ? `${broker.latency} ms` : "Unavailable"} />
          <ConnectionFact label="Running bots" value={`${broker.runningBots ?? adapters.filter((adapter) => adapter.running).length} / ${broker.botCount ?? adapters.length}`} />
          <ConnectionFact label="Last checked" value={formatHeartbeat(broker.heartbeat)} />
        </Grid>
      </Grid>
      <Box mt="5" pt="4" borderTopWidth="1px" borderColor="border.default">
        <Flex mb="3" justify="space-between" align="center" gap="3"><Text fontSize="xs" fontWeight="750">Bot consumers using this account</Text><StatusPill>{adapters.length} {adapters.length === 1 ? "ADAPTER" : "ADAPTERS"}</StatusPill></Flex>
        <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap="2">
          {adapters.map((adapter) => (
            <Flex key={adapter.botId} p="3" bg="bg.subtle" borderRadius="4px" align="center" gap="3">
              <Flex boxSize="30px" align="center" justify="center" borderRadius="4px" bg="bg.panel"><Box as={Bot} boxSize="3.5" color="fg.muted" /></Flex>
              <Box flex="1" minW="0"><Text fontSize="xs" fontWeight="700" truncate>{botDisplayName(adapter.botId)}</Text><Text mt=".5" fontSize="9px" color="fg.subtle" fontFamily="mono" truncate>{adapter.botId}</Text></Box>
              <StatusPill tone={adapter.running ? "positive" : "neutral"}>{adapter.status.toUpperCase()}</StatusPill>
            </Flex>
          ))}
        </Grid>
      </Box>
    </Panel>
  );
}

function BrokerLoadingState() {
  return (
    <Stack gap="4" aria-label="Loading cTrader connection">
      <Grid templateColumns={{ base: "1fr 1fr", lg: "repeat(4,1fr)" }} gap="3">
        {[0, 1, 2, 3].map((item) => <Panel key={item} p="4"><Skeleton height="12px" width="45%" /><Skeleton mt="3" height="28px" width="62%" /><Skeleton mt="3" height="10px" width="80%" /></Panel>)}
      </Grid>
      <Panel icon={Landmark} title="Connecting to cTrader" action={<HStackLoader />}>
        <Grid templateColumns={{ base: "1fr", lg: "1fr 1.3fr" }} gap="5">
          <Stack gap="3"><Skeleton height="11px" width="34%" /><Skeleton height="34px" width="48%" /><Skeleton height="24px" width="70%" /><Skeleton height="44px" /></Stack>
          <Grid templateColumns="repeat(3,1fr)" gap="2">{[0, 1, 2, 3, 4, 5].map((item) => <Skeleton key={item} height="62px" borderRadius="4px" />)}</Grid>
        </Grid>
      </Panel>
    </Stack>
  );
}

function HStackLoader() {
  return <Flex align="center" gap="2"><Spinner size="xs" color="status.info" /><Text fontSize="10px" color="fg.muted">PROBING</Text></Flex>;
}

function ConnectionFact({ label, value }: { label: string; value: string }) {
  return <Box p="3" borderRadius="4px" bg="bg.subtle"><Text fontSize="9px" color="fg.subtle" textTransform="uppercase" letterSpacing=".05em">{label}</Text><Text mt="1.5" fontFamily="mono" fontSize="xs" fontWeight="800" overflowWrap="anywhere">{value}</Text></Box>;
}

function CredentialFact({ icon: Icon, title, detail }: { icon: typeof KeyRound; title: string; detail: string }) {
  return <Flex p="3" gap="3" bg="bg.subtle" borderRadius="4px" align="start"><Flex boxSize="30px" flexShrink="0" align="center" justify="center" borderRadius="4px" bg="bg.panel"><Box as={Icon} boxSize="3.5" color="status.info" /></Flex><Box><Text fontSize="xs" fontWeight="700">{title}</Text><Text mt="1" fontSize="10px" color="fg.muted" lineHeight="1.6">{detail}</Text></Box></Flex>;
}

function botDisplayName(botId: string): string {
  if (botId.includes("multi-edge")) return "XAU Multi-Edge Portfolio";
  if (botId.includes("session-momentum")) return "XAU Session Momentum";
  return botId;
}

function formatHeartbeat(value: string): string {
  const time = Date.parse(value);
  return Number.isFinite(time) ? new Date(time).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : value;
}

function formatProbeTime(value: number): string {
  return new Date(value).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

function KeyValue({ k, v }: { k: string; v: string }) {
  return <Flex justify="space-between" gap="3"><Text fontSize="10px" color="fg.muted">{k}</Text><Text fontSize="10px" fontFamily="mono" textAlign="right">{v}</Text></Flex>;
}

export function MarketDataPage() {
  const { result, query } = useList<DataQualityRecord>({ resource: "dataQuality", pagination: { currentPage: 1, pageSize: 10_000 }, liveMode: "auto", queryOptions: { refetchInterval: 5_000 } });
  const rows = result.data ?? [];
  const healthyCount = rows.filter((feed) => feed.status === "HEALTHY").length;
  const degradedCount = rows.filter((feed) => feed.status === "DEGRADED").length;
  const pausedCount = rows.filter((feed) => feed.status === "PAUSED").length;
  const closedCount = rows.filter((feed) => feed.status === "CLOSED").length;
  const marketClosed = rows.some((feed) => feed.marketSession === "CLOSED" || feed.expectedClosure);
  const latestCandleMs = rows.map((feed) => Date.parse(feed.lastCandle)).filter(Number.isFinite).sort((a, b) => b - a)[0];
  const overallLabel = query.isLoading ? "SYNCING" : degradedCount > 0 ? "DATA DEGRADED" : healthyCount > 0 ? "FEED ACTIVE" : closedCount > 0 ? "MARKET CLOSED" : pausedCount === rows.length && rows.length > 0 ? "ALL PAUSED" : "NO DATA";
  const overallTone = query.isLoading ? "neutral" : degradedCount > 0 ? "warning" : healthyCount > 0 ? "positive" : closedCount > 0 ? "info" : pausedCount > 0 ? "neutral" : "negative";
  return (
    <Stack gap="5">
      <PageHeader
        title="Market Data Health"
        description="Finalized cTrader candle freshness, session state and persisted feed safeguards."
        actions={
          <>
            <StatusPill tone={overallTone}>{overallLabel}</StatusPill>
            <Button variant="outline" size="sm" onClick={() => query.refetch()} disabled={query.isFetching} minW="118px">
              {query.isFetching ? <><Spinner size="xs" />Refreshing...</> : <><Box as={RefreshCw} boxSize="4" />Refresh</>}
            </Button>
          </>
        }
      />

      {query.isFetching && !query.isLoading && (
        <Flex px="3.5" py="2.5" borderWidth="1px" borderColor="rgba(59,130,246,.24)" bg="rgba(59,130,246,.07)" borderRadius="4px" align="center" gap="2.5">
          <Spinner size="xs" color="status.info" />
          <Text fontSize="xs" color="fg.muted">Refreshing persisted feed state...</Text>
        </Flex>
      )}

      {query.error && <Panel icon={TriangleAlert} title="Market data request failed" action={<StatusPill tone="negative">OFFLINE</StatusPill>}><Text color="status.negative" fontSize="xs">{query.error.message}</Text></Panel>}

      {query.isLoading ? (
        <MarketDataLoadingState />
      ) : rows.length === 0 ? (
        <Panel><EmptyState icon={DatabaseZap} title="No registered market feeds" description="Register a cTrader bot before monitoring finalized candle freshness." /></Panel>
      ) : (
        <>
          <Grid templateColumns={{ base: "1fr 1fr", lg: "repeat(4,1fr)" }} gap="3">
            <MetricCard icon={Radio} label="Registered feeds" value={String(rows.length)} detail={`${new Set(rows.map((feed) => feed.timeframe)).size} finalized timeframes`} />
            <MetricCard icon={CheckCircle2} label="Action required" value={String(degradedCount)} detail={degradedCount === 0 ? "No open-session feed faults" : `${degradedCount} degraded feed${degradedCount === 1 ? "" : "s"}`} />
            <MetricCard icon={CalendarClock} label="Market session" value={marketClosed ? "CLOSED" : "OPEN"} detail={marketClosed ? "Expected XAU weekend closure" : "Freshness guard is active"} />
            <MetricCard icon={Clock3} label="Latest finalized" value={latestCandleMs ? formatCompactCandleTime(latestCandleMs) : "—"} detail={latestCandleMs ? formatDuration(Date.now() - latestCandleMs) + " ago" : "No finalized candle observed"} />
          </Grid>

          {marketClosed && (
            <Flex bg="rgba(59,130,246,.06)" borderWidth="1px" borderColor="rgba(59,130,246,.2)" borderRadius="4px" p="4" gap="3" align={{ base: "start", md: "center" }} direction={{ base: "column", md: "row" }}>
              <Flex boxSize="34px" flexShrink="0" align="center" justify="center" borderRadius="4px" bg="rgba(59,130,246,.12)" color="status.info"><Box as={CalendarClock} boxSize="4" /></Flex>
              <Box flex="1"><Text fontSize="sm" fontWeight="750">XAUUSD is in its expected weekend closure</Text><Text mt="1" fontSize="xs" color="fg.muted">The Friday candle remains the latest finalized event until the cTrader session reopens. This expected closure is not reported as feed degradation.</Text></Box>
              <StatusPill tone="info">FRESHNESS PAUSED</StatusPill>
            </Flex>
          )}

          <Panel icon={DatabaseZap} title="Finalized feed subscriptions" action={<StatusPill>{rows.length} {rows.length === 1 ? "FEED" : "FEEDS"}</StatusPill>}>
            <Grid templateColumns={{ base: "1fr", xl: "1fr 1fr" }} gap="3" opacity={query.isFetching ? 0.72 : 1} transition="opacity .18s ease">
              {rows.map((feed) => <MarketFeedCard key={feed.id} feed={feed} />)}
            </Grid>
          </Panel>

          <Panel icon={ShieldCheck} title="Finalized-candle contract" action={<StatusPill tone="positive">SERVER ENFORCED</StatusPill>}>
            <Grid templateColumns={{ base: "1fr", md: "repeat(3,1fr)" }} gap="3">
              <CredentialFact icon={CheckCircle2} title="Finalized events only" detail="Strategies receive broker-confirmed closed candles; the UI does not synthesize or interpolate missing market data." />
              <CredentialFact icon={TimerReset} title="Per-feed stale guard" detail="Each bot applies its persisted maximum data age while the market session is open." />
              <CredentialFact icon={CirclePause} title="Fail closed" detail="Stale, gap or operator pause reasons are persisted by the server and surfaced on the matching feed." />
            </Grid>
          </Panel>
        </>
      )}
    </Stack>
  );
}

function MarketFeedCard({ feed }: { feed: DataQualityRecord }) {
  const tone = feed.status === "HEALTHY" ? "positive" : feed.status === "DEGRADED" ? "warning" : feed.status === "CLOSED" ? "info" : "neutral";
  const label = feed.status === "CLOSED" ? "MARKET CLOSED" : feed.status;
  const lastCandleMs = Date.parse(feed.lastCandle);
  return (
    <Box p="4" borderWidth="1px" borderColor="border.default" borderRadius="4px" bg="bg.subtle">
      <Flex justify="space-between" align="start" gap="3">
        <HStackFeed icon={feed.status === "PAUSED" ? CirclePause : feed.status === "DEGRADED" ? TriangleAlert : Radio} title={`${feed.symbol} · ${feed.timeframe}`} detail={formatFeedName(feed.feed)} />
        <StatusPill tone={tone}>{label}</StatusPill>
      </Flex>
      <Grid mt="4" templateColumns={{ base: "1fr 1fr", md: "repeat(3,1fr)" }} gap="2">
        <FeedFact label="Last finalized" value={Number.isFinite(lastCandleMs) ? formatCandleTime(lastCandleMs) : "Not observed"} />
        <FeedFact label="Candle age" value={feed.latencyMs === undefined ? "Unavailable" : formatDuration(feed.latencyMs)} />
        <FeedFact label="Stale guard" value={feed.maxDataAgeMs === undefined ? "Unavailable" : formatDuration(feed.maxDataAgeMs)} />
        <FeedFact label="Recorded gaps" value={String(feed.unexpectedGaps)} />
        <FeedFact label="Missing candles" value={String(feed.missingCandles)} />
        <FeedFact label="Session" value={feed.marketSession ?? "UNKNOWN"} />
      </Grid>
      {feed.status === "CLOSED" && <FeedNotice tone="info" text="Expected weekend closure · freshness checks resume when the XAU session opens." />}
      {feed.status === "PAUSED" && <FeedNotice tone="neutral" text={`Persisted pause: ${humanizeCode(feed.gapCode ?? "No pause reason")}`} />}
      {feed.status === "DEGRADED" && <FeedNotice tone="warning" text="The latest finalized candle exceeded this feed's stale guard while the market session was open." />}
    </Box>
  );
}

function HStackFeed({ icon: Icon, title, detail }: { icon: typeof Radio; title: string; detail: string }) {
  return <Flex align="center" gap="3" minW="0"><Flex boxSize="34px" flexShrink="0" align="center" justify="center" borderRadius="4px" bg="bg.panel"><Box as={Icon} boxSize="4" color="brand.400" /></Flex><Box minW="0"><Text fontSize="sm" fontWeight="800" truncate>{title}</Text><Text mt=".5" fontSize="10px" color="fg.muted" truncate>{detail}</Text></Box></Flex>;
}

function FeedFact({ label, value }: { label: string; value: string }) {
  return <Box p="3" borderRadius="4px" bg="bg.panel"><Text fontSize="9px" color="fg.subtle" textTransform="uppercase" letterSpacing=".05em">{label}</Text><Text mt="1.5" fontFamily="mono" fontSize="xs" fontWeight="800" overflowWrap="anywhere">{value}</Text></Box>;
}

function FeedNotice({ tone, text }: { tone: "info" | "warning" | "neutral"; text: string }) {
  const colors = tone === "info" ? { bg: "rgba(59,130,246,.07)", border: "rgba(59,130,246,.2)", color: "status.info" } : tone === "warning" ? { bg: "rgba(245,158,11,.07)", border: "rgba(245,158,11,.2)", color: "status.warning" } : { bg: "bg.panel", border: "border.default", color: "fg.muted" };
  return <Flex mt="3" p="3" borderWidth="1px" borderColor={colors.border} bg={colors.bg} borderRadius="4px" align="center" gap="2"><Box as={tone === "warning" ? TriangleAlert : tone === "info" ? CalendarClock : CirclePause} boxSize="3.5" color={colors.color} /><Text fontSize="10px" color="fg.muted">{text}</Text></Flex>;
}

function MarketDataLoadingState() {
  return <Stack gap="4" aria-label="Loading market data health"><Grid templateColumns={{ base: "1fr 1fr", lg: "repeat(4,1fr)" }} gap="3">{[0, 1, 2, 3].map((item) => <Panel key={item} p="4"><Skeleton height="12px" width="48%" /><Skeleton mt="3" height="28px" width="64%" /><Skeleton mt="3" height="10px" width="82%" /></Panel>)}</Grid><Panel icon={DatabaseZap} title="Loading feed subscriptions" action={<HStackLoader />}><Grid templateColumns={{ base: "1fr", xl: "1fr 1fr" }} gap="3">{[0, 1].map((item) => <Skeleton key={item} height="238px" borderRadius="4px" />)}</Grid></Panel></Stack>;
}

function formatFeedName(value: string): string {
  return value.replace(/ctrader/i, "cTrader");
}

function formatDuration(valueMs: number): string {
  if (!Number.isFinite(valueMs) || valueMs < 0) return "Unavailable";
  if (valueMs < 1_000) return `${Math.round(valueMs)} ms`;
  const seconds = Math.floor(valueMs / 1_000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ${minutes % 60}m`;
  return `${Math.floor(hours / 24)}d ${hours % 24}h`;
}

function formatCandleTime(valueMs: number): string {
  return new Date(valueMs).toLocaleString([], { dateStyle: "medium", timeStyle: "short" });
}

function formatCompactCandleTime(valueMs: number): string {
  return new Date(valueMs).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function humanizeCode(value: string): string {
  if (value === "SWITCH_TO_MULTI_EDGE_PAPER") return "Switched to Multi-Edge paper bot";
  return value.toLowerCase().replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export function DataQualityPage() {
  const { result, query } = useList<DataQualityRecord>({ resource: "dataQuality", pagination: { currentPage: 1, pageSize: 10_000 }, liveMode: "auto", queryOptions: { refetchInterval: 5_000 } });
  const rows = result.data ?? [];
  const degradedCount = rows.filter((feed) => feed.status === "DEGRADED").length;
  const pausedCount = rows.filter((feed) => feed.status === "PAUSED").length;
  const closedCount = rows.filter((feed) => feed.status === "CLOSED").length;
  const observedCount = rows.filter((feed) => Number.isFinite(Date.parse(feed.lastCandle))).length;
  const recordedGaps = rows.reduce((total, feed) => total + feed.unexpectedGaps, 0);
  const missingCandles = rows.reduce((total, feed) => total + feed.missingCandles, 0);
  const marketClosed = rows.some((feed) => feed.marketSession === "CLOSED" || feed.expectedClosure);
  const overallLabel = query.isLoading ? "SYNCING" : degradedCount > 0 ? "QUALITY ALERT" : marketClosed ? "MARKET CLOSED" : pausedCount === rows.length && rows.length > 0 ? "ALL PAUSED" : "QUALITY HEALTHY";
  const overallTone = query.isLoading ? "neutral" : degradedCount > 0 ? "warning" : marketClosed ? "info" : pausedCount === rows.length && rows.length > 0 ? "neutral" : "positive";
  const cols: Column<DataQualityRecord>[] = [
    { key: "feed", header: "Feed", mobilePriority: "primary", render: (row) => <Flex align="center" gap="2.5" minW="150px"><Flex boxSize="30px" flexShrink="0" align="center" justify="center" borderRadius="4px" bg="bg.subtle"><Box as={row.status === "PAUSED" ? CirclePause : Radio} boxSize="3.5" color={row.status === "DEGRADED" ? "status.warning" : "brand.400"} /></Flex><Box minW="0"><Text fontWeight="750" truncate>{formatFeedName(row.feed)}</Text><Text fontSize="10px" color="fg.muted">{row.symbol} · {feedConsumerName(row.id)}</Text></Box></Flex> },
    { key: "timeframe", header: "Timeframe", mobilePriority: "secondary", render: (row) => <Text fontFamily="mono" fontSize="sm" fontWeight="800">{row.timeframe}</Text> },
    { key: "lastCandle", header: "Last finalized", mobilePriority: "secondary", render: (row) => <FinalizedEventCell feed={row} /> },
    { key: "latency", header: "Freshness", mobilePriority: "primary", render: (row) => <FreshnessCell feed={row} /> },
    { key: "unexpectedGaps", header: "Quality events", hideBelow: "lg", render: (row) => <Box textAlign="right"><Text fontFamily="mono" fontWeight="800">{row.unexpectedGaps}</Text><Text mt=".5" fontSize="9px" color="fg.subtle">{row.missingCandles} missing</Text></Box> },
    { key: "gapCode", header: "Persisted reason", hideBelow: "xl", render: (row) => <Text maxW="170px" fontSize="10px" color={row.gapCode ? "fg.default" : "fg.subtle"}>{row.gapCode ? humanizeCode(row.gapCode) : "No pause reason"}</Text> },
    { key: "status", header: "Status", render: (row) => <StatusPill tone={row.status === "HEALTHY" ? "positive" : row.status === "CLOSED" ? "info" : row.status === "PAUSED" ? "negative" : "warning"}>{row.status === "CLOSED" ? "MARKET CLOSED" : row.status}</StatusPill> },
  ];
  return (
    <Stack gap="5">
      <PageHeader
        title="Data Quality Ledger"
        description="Persisted finalized-candle freshness, quality events and fail-closed reasons—without synthesized data."
        actions={
          <>
            <StatusPill tone={overallTone}>{overallLabel}</StatusPill>
            <Button variant="outline" size="sm" onClick={() => query.refetch()} disabled={query.isFetching} minW="118px">
              {query.isFetching ? <><Spinner size="xs" />Refreshing...</> : <><Box as={RefreshCw} boxSize="4" />Refresh</>}
            </Button>
          </>
        }
      />

      {query.error && <Panel icon={TriangleAlert} title="Data-quality request failed" action={<StatusPill tone="negative">OFFLINE</StatusPill>}><Text color="status.negative" fontSize="xs">{query.error.message}</Text></Panel>}

      {query.isLoading ? (
        <DataQualityLoadingState />
      ) : rows.length === 0 ? (
        <Panel><EmptyState icon={DatabaseZap} title="No persisted feed quality" description="Register a bot and receive finalized market events before inspecting feed quality." /></Panel>
      ) : (
        <>
          <Grid templateColumns={{ base: "1fr 1fr", lg: "repeat(4,1fr)" }} gap="3">
            <MetricCard icon={Radio} label="Registered feeds" value={String(rows.length)} detail={`${new Set(rows.map((feed) => feed.timeframe)).size} monitored timeframes`} />
            <MetricCard icon={CheckCircle2} label="Finalized coverage" value={`${observedCount} / ${rows.length}`} detail="Feeds with a persisted final candle" />
            <MetricCard icon={TriangleAlert} label="Action required" value={String(degradedCount)} detail={degradedCount === 0 ? "No open-session feed faults" : `${degradedCount} degraded feed${degradedCount === 1 ? "" : "s"}`} />
            <MetricCard icon={Activity} label="Quality events" value={String(recordedGaps + missingCandles)} detail={`${recordedGaps} gaps · ${missingCandles} missing candles`} />
          </Grid>

          {marketClosed && (
            <Flex bg="rgba(59,130,246,.06)" borderWidth="1px" borderColor="rgba(59,130,246,.2)" borderRadius="4px" p="4" gap="3" align={{ base: "start", md: "center" }} direction={{ base: "column", md: "row" }}>
              <Flex boxSize="34px" flexShrink="0" align="center" justify="center" borderRadius="4px" bg="rgba(59,130,246,.12)" color="status.info"><Box as={CalendarClock} boxSize="4" /></Flex>
              <Box flex="1"><Text fontSize="sm" fontWeight="750">Freshness guard is paused for the expected XAU closure</Text><Text mt="1" fontSize="xs" color="fg.muted">Candle age continues to display as an observed number, but it does not consume the one-minute stale budget until the market session reopens.</Text></Box>
              <StatusPill tone="info">SESSION CLOSED</StatusPill>
            </Flex>
          )}

          <Panel icon={DatabaseZap} title="Feed quality ledger" action={<StatusPill>{rows.length} {rows.length === 1 ? "FEED" : "FEEDS"}</StatusPill>}>
            <DataTable rows={rows} columns={cols} rowLabel="feeds" empty="No registered feeds" pageSize={10} />
          </Panel>

          <Panel icon={ShieldCheck} title="Fail-closed policy" action={<StatusPill tone="positive">SERVER ENFORCED</StatusPill>}>
            <Grid templateColumns={{ base: "1fr", md: "repeat(3,1fr)" }} gap="3">
              <CredentialFact icon={CheckCircle2} title="No synthetic candles" detail="Only cTrader-confirmed finalized events enter persisted runtime state and this quality ledger." />
              <CredentialFact icon={TimerReset} title="Numeric freshness budget" detail="Open-session candle age is compared with each feed's persisted maximum data age." />
              <CredentialFact icon={CirclePause} title="Persistent pause reason" detail="Gap, stale-data and operator pauses survive restart and stay attached to the affected feed." />
            </Grid>
          </Panel>
        </>
      )}
    </Stack>
  );
}

function FinalizedEventCell({ feed }: { feed: DataQualityRecord }) {
  const valueMs = Date.parse(feed.lastCandle);
  if (!Number.isFinite(valueMs)) return <Box><Text fontSize="xs" fontWeight="700">Not observed</Text><Text mt=".5" fontSize="9px" color="fg.subtle">No finalized candle</Text></Box>;
  const value = new Date(valueMs);
  return <Box minW="130px"><Text fontFamily="mono" fontSize="xs" fontWeight="750">{value.toLocaleDateString([], { dateStyle: "medium" })}</Text><Text mt=".5" fontFamily="mono" fontSize="9px" color="fg.muted">{value.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</Text></Box>;
}

function feedConsumerName(botId: string): string {
  if (botId.includes("multi-edge")) return "Multi-Edge";
  if (botId.includes("session-momentum")) return "Momentum";
  return botId;
}

function FreshnessCell({ feed }: { feed: DataQualityRecord }) {
  if (feed.latencyMs === undefined) return <Box minW="150px"><Text fontFamily="mono" fontSize="sm" fontWeight="800">Unavailable</Text><Text mt="1" fontSize="9px" color="fg.subtle">{feed.status === "PAUSED" ? "Feed is paused" : "No candle observed"}</Text></Box>;
  const limit = feed.maxDataAgeMs ?? 0;
  const usage = limit > 0 ? feed.latencyMs / limit : 0;
  const tone = usage > 1 ? "negative" : usage >= 0.8 ? "warning" : "positive";
  const guardPaused = feed.status === "CLOSED" || feed.marketSession === "CLOSED";
  return (
    <Box minW="165px">
      <Text fontFamily="mono" fontSize="sm" fontWeight="850">{formatDuration(feed.latencyMs)}</Text>
      {guardPaused ? (
        <Flex mt="1.5" align="center" gap="1.5"><Box as={CalendarClock} boxSize="3" color="status.info" /><Text fontSize="9px" color="status.info">Guard paused · expected closure</Text></Flex>
      ) : limit > 0 ? (
        <><Box mt="2"><DataBar value={feed.latencyMs} max={limit} tone={tone} /></Box><Text mt="1" fontSize="9px" color="fg.subtle">{Math.round(usage * 100)}% of {formatDuration(limit)} stale limit</Text></>
      ) : <Text mt="1" fontSize="9px" color="fg.subtle">No stale limit persisted</Text>}
    </Box>
  );
}

function DataQualityLoadingState() {
  return <Stack gap="4" aria-label="Loading data quality"><Grid templateColumns={{ base: "1fr 1fr", lg: "repeat(4,1fr)" }} gap="3">{[0, 1, 2, 3].map((item) => <Panel key={item} p="4"><Skeleton height="12px" width="48%" /><Skeleton mt="3" height="28px" width="64%" /><Skeleton mt="3" height="10px" width="82%" /></Panel>)}</Grid><Panel icon={DatabaseZap} title="Loading quality ledger" action={<HStackLoader />}><Stack gap="2">{[0, 1].map((item) => <Skeleton key={item} height="68px" borderRadius="4px" />)}</Stack></Panel></Stack>;
}
